// Pure rule for WHICH empty state the UnifiedContactSelector shows when its list
// resolves to zero rows. Split out of the component (which drags React + MUI +
// virtuoso) so it can be unit-tested in the node jest env with no DOM.
//
// FE#305: audience creation silently no-ops when the WhatsApp-contacts source is
// empty because the account has 0 connected engines. The picker used to show a
// bland "No contacts available" with a disabled Import button and nothing telling
// the user the real fix is to connect WhatsApp. This rule adds an explicit,
// actionable 'connect-whatsapp' state — but ONLY in the audience-import context
// (promptConnectWhenEmpty), so every other picker keeps its existing copy.

export type ContactPickerEmptyState =
    /** A search is active and matched nothing (there IS a live connection). */
    | 'search-miss'
    /** Import context, no live WhatsApp connection → tell the user to connect. */
    | 'connect-whatsapp'
    /** Connected but the address book is empty (or a non-import picker). */
    | 'no-contacts';

export interface ContactPickerEmptyStateInput {
    /** The search box has a query. */
    hasSearch: boolean;
    /** A live WhatsApp connection exists that can supply contacts (gateway OR web). */
    hasWhatsAppConnection: boolean;
    /** Caller is the audience-import picker, which wants the connect prompt. */
    promptConnectWhenEmpty: boolean;
}

/**
 * The connect prompt takes priority over a search miss: when there is no live
 * connection in the import context, the ROOT cause of the empty list is the
 * missing engine, not the query — telling the user "no contacts found" for their
 * search would hide the thing they actually need to fix.
 */
export const resolveContactPickerEmptyState = ({
    hasSearch,
    hasWhatsAppConnection,
    promptConnectWhenEmpty,
}: ContactPickerEmptyStateInput): ContactPickerEmptyState => {
    if (promptConnectWhenEmpty && !hasWhatsAppConnection) return 'connect-whatsapp';
    if (hasSearch) return 'search-miss';
    return 'no-contacts';
};
